/**
 * The canonical assignable membership roles — MIRRORS the server's
 * `api.workspace_admin_service.assignable_roles` (the closed allowlist the
 * backend validates a role against). One source of truth for the member role
 * picker (and, ideally, the invite dialog) so the two never drift from the
 * server contract.
 */
export const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "viewer", label: "Viewer" },
  { value: "commenter", label: "Commenter" },
  { value: "editor", label: "Editor" },
  { value: "reviewer", label: "Reviewer" },
  { value: "author", label: "Author" },
  { value: "workspace-admin", label: "Workspace admin" },
  { value: "staff", label: "Staff" },
];

/** Is `role` one the client may assign? (defence-in-depth; the server is
 *  authoritative). */
export function isAssignableRole(role: string): boolean {
  return ROLE_OPTIONS.some((o) => o.value === role);
}

/**
 * The canonical permission-matrix vocabulary — MIRRORS the server's
 * `auth.capabilities` (the `Capability` / `Role` enums + the static default
 * matrix). This is the presentation source of truth for the permission MATRIX
 * (roles × capabilities grid of toggles). The SERVER is authoritative — the
 * matrix here drives the UI + the mock adapter; the real toggles PUT to the
 * staff `AdminOps` surface which re-evaluates via `authz.policy`.
 */

/** One matrix COLUMN — a workspace-scoped capability the toggles flip. */
export interface CapabilityMeta {
  key: string;
  label: string;
  hint: string;
}

/** The matrix columns, in display order (the eleven workspace-scoped capabilities). */
export const CAPABILITIES: CapabilityMeta[] = [
  { key: "view_doc", label: "View", hint: "Read documents + delivered evidence" },
  { key: "comment", label: "Comment", hint: "Create comments and replies" },
  { key: "resolve_comment", label: "Resolve", hint: "Change a comment's review status (open → resolved / acted-on)" },
  { key: "edit_doc", label: "Edit", hint: "Whole-resource / section edits" },
  { key: "propose_edit", label: "Propose", hint: "Create a pending proposed revision" },
  { key: "accept_proposal", label: "Accept", hint: "Accept / reject a proposed revision" },
  { key: "export_deliver", label: "Export", hint: "Trigger export when the delivery gate is green" },
  {
    key: "restore_version",
    label: "Restore",
    hint: "Put a past version back as the head — rewrites a delivered document, moves the delivery-gate hash and stales pending proposals",
  },
  { key: "manage_members", label: "Members", hint: "Invite / change role / remove / suspend members" },
  { key: "manage_permissions", label: "Permissions", hint: "Edit the role × capability matrix" },
  { key: "admin_workspace", label: "Workspace", hint: "Rename / recolor workspace, workspace settings" },
];

/** The matrix ROWS, in privilege order. `owner` is locked-on. */
export const MATRIX_ROLES: { key: string; label: string; locked?: boolean }[] = [
  { key: "viewer", label: "Viewer" },
  { key: "commenter", label: "Commenter" },
  { key: "reviewer", label: "Reviewer" },
  { key: "editor", label: "Editor" },
  { key: "workspace_admin", label: "Workspace admin" },
  { key: "owner", label: "Owner", locked: true },
];

const ALL_WS_CAPS = CAPABILITIES.map((c) => c.key);

/** The static default matrix (role → the capabilities it grants by default). */
export const DEFAULT_MATRIX: Record<string, string[]> = {
  viewer: ["view_doc"],
  commenter: ["view_doc", "comment"],
  reviewer: ["view_doc", "comment", "resolve_comment"],
  editor: [
    "view_doc",
    "comment",
    "resolve_comment",
    "edit_doc",
    "propose_edit",
    "accept_proposal",
    "export_deliver",
  ],
  workspace_admin: ALL_WS_CAPS,
  owner: ALL_WS_CAPS,
};

/**
 * Absorb the historical role-string drift into the canonical vocabulary —
 * the mirror of `auth.capabilities.parseRole` (`workspace-admin`, `author`,
 * `staff`, `client`). Stored membership rows still carry the hyphenated and
 * legacy spellings, so any client-side capability check must normalise first or
 * it silently denies a real workspace admin.
 */
export function normalizeRole(role: string): string {
  switch (role) {
    case "workspace-admin":
      return "workspace_admin";
    case "author":
      return "editor";
    case "staff":
      return "workspace_admin";
    case "client":
      return "commenter";
    default:
      return role;
  }
}

/** The static default grant for (role, capability). Owner is always allowed. */
export function defaultAllowed(role: string, capability: string): boolean {
  const r = normalizeRole(role);
  if (r === "owner") return true;
  return (DEFAULT_MATRIX[r] ?? []).includes(capability);
}

// ── membership → capability (the affordance gate) ───────────────────────────
//
// The SERVER is authoritative on every one of these decisions; this is only
// what the client uses to decide whether to RENDER an affordance. Handing
// someone a live-looking control whose every write the server rejects is the
// decoration `People.authority.test.tsx` pins shut — the same rule applies to
// the create-article action and to its "write it in the editor" mode.

/** ASSIGNABLE role (`lib/roles.ROLE_OPTIONS`, mirroring the server allowlist) →
 *  MATRIX row (`MATRIX_ROLES`). The two vocabularies are NOT identical and never
 *  were: a membership can carry `workspace-admin` (hyphen), `staff` or `author`,
 *  none of which is a matrix key. Mapping them explicitly is what stops a silent
 *  `DEFAULT_MATRIX[role] ?? []` → "no capabilities" for a workspace admin. */
const ROLE_TO_MATRIX_ROW: Record<string, string> = {
  "workspace-admin": "workspace_admin",
  // A workspace `staff` membership is the in-room superuser (it is the predicate
  // `Workspace.tsx`/`Settings.tsx` already treat as full authority).
  staff: "owner",
  // An author writes the article — the editor row is its capability shape.
  author: "editor",
};

/** Does `role` (an ASSIGNABLE membership role) grant `capability` by default? */
export function roleAllows(role: string, capability: string): boolean {
  return defaultAllowed(ROLE_TO_MATRIX_ROW[role] ?? role, capability);
}

/** The caller's role in `slug`, or `null` when they are not a member. */
export function roleIn(
  memberships: readonly { workspace_slug: string; role: string }[],
  slug: string,
): string | null {
  return memberships.find((m) => m.workspace_slug === slug)?.role ?? null;
}

/** Is the caller a member of `slug`? Global staff count — they reach every
 *  workspace (the server's own membership gate treats them the same way). */
export function isMemberOf(
  isStaff: boolean,
  memberships: readonly { workspace_slug: string; role: string }[],
  slug: string,
): boolean {
  return isStaff || roleIn(memberships, slug) !== null;
}

/** Does the caller hold `capability` in `slug`? Global staff always do. */
export function canInWorkspace(
  isStaff: boolean,
  memberships: readonly { workspace_slug: string; role: string }[],
  slug: string,
  capability: string,
): boolean {
  if (isStaff) return true;
  const role = roleIn(memberships, slug);
  return role !== null && roleAllows(role, capability);
}
